/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { Locale, LocaleKeys } from '../../../core/locale';
import { CursorManager, KeyManager } from '../../../core/navigation';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import { setListEntryFocus } from '../../../store/list/list.actions';
import { setAccountOptionsList } from '../../../store/settings/account/account.reducers';
import { List, Mousetrap, ScreenPreview } from '../../common';
import { SettingsTemplate } from '../SettingsTemplate';

import '../SettingsCommon.scss';

const AccountScreen: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    const [isCursorActive, setisCursorActive] = useState(false);
    const { optionsList, selectedOption } = useTivoSelector((state: RootState) => state.account);

    const onCursorStateChangeCallback = (state) => {
        setisCursorActive(state);
        if (!isCursorActive) dispatch(setListEntryFocus(ListTypes.ACCOUNT, true));
    };

    const onNetworkStateChangeCallback = () => {
        dispatch(setAccountOptionsList());
    };

    const selectedOptionIndex = useMemo(() => {
        return optionsList.reduce(
            (acc, option, index) => (option.title === selectedOption?.title ? index : acc),
            0,
        );
    }, [optionsList, selectedOption]);

    useEffect(() => {
        dispatch(setAccountOptionsList());
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const refNetworkManager = NetworkManager.subscribe(onNetworkStateChangeCallback);

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            CursorManager.unsubscribe(refCursorManager);
            NetworkManager.unsubscribe(refNetworkManager);
        };
    }, []);

    const renderAccountList = () => {
        return (
            <div className="settings-common-content">
                <List
                    type={ListTypes.ACCOUNT}
                    itemList={optionsList}
                    initialFocusedIndex={selectedOptionIndex}
                    initialSelectedIndex={selectedOptionIndex}
                    generateItemView={(item: any, itemClass, index) => (
                        <Mousetrap
                            key={index}
                            value={`${index}_${ListTypes.ACCOUNT}`}
                            className={`item ${itemClass}`}
                        >
                            <span>{item.title}</span>
                        </Mousetrap>
                    )}
                />
            </div>
        );
    };

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.SETTINGS_ACCOUNT).toUpperCase()}>
            {renderAccountList()}
            <ScreenPreview>
                <div>{selectedOption?.preview}</div>
            </ScreenPreview>
        </SettingsTemplate>
    );
};

export default AccountScreen;
